/** Human-readable labels for API camelCase keys */
export const METRIC_LABEL_OVERRIDES = {
  totalQAQCItems: 'Total QAQC items',
  totalBGCount: 'Total BG count',
  totalInsuranceCount: 'Total insurance count',
  totalRABills: 'Total RA bills',
  totalMilestones: 'Total milestones',
  totalEOTRequests: 'Total EOT requests',
  totalDrawings: 'Total drawings',
  totalDeviations: 'Total deviations',
  totalClearanceItems: 'Total clearance items',
  totalCorrespondenceEntries: 'Correspondence entries (period)',
  totalCorrespondenceChains: 'Correspondence Subjects',
  totalOpenChains: 'Open Subjects',
  totalClosedChains: 'Closed Subjects',
  totalCriticalChains: 'Critical Subjects',
  totalOverdueTargetedReplies: 'Targeted reply overdue',
  totalRABillGrossValue: 'RA cumulative gross (latest/stream)',
  totalRABillNetValue: 'RA net value',
  totalRABillCertifiedValue: 'RA certified value',
  totalRABillReceivedValue: 'RA received value',
  totalRABillPendingValue: 'RA pending value',
  totalBGValue: 'Total BG value',
  totalExtraItems: 'Extra items',
  totalSpecializedItems: 'Specialized items',
  correspondenceInward: 'Inward (period)',
  totalInward: 'Total inward (period)',
  totalOutward: 'Total outward (period)',
  correspondenceOutward: 'Outward (period)',
  correspondenceAttachmentCount: 'Attachments (period)',
  activeBGCount: 'Active BG count',
  insuranceCount: 'Insurance count',
  milestonesPending: 'Milestones pending',
  milestonesPlannedInPeriod: 'Milestones planned in period',
  milestonesAchievedInPeriod: 'Milestones achieved in period',
  approvedEOT: 'Approved EOT',
  pendingEOT: 'Pending EOT',
  rejectedEOT: 'Rejected EOT',
  raised: 'Raised',
  approved: 'Approved',
  rejected: 'Rejected',
  closed: 'Closed',
  pending: 'Pending',
  submitted: 'Submitted',
  internal: 'Internal',
  external: 'External',
  total: 'Total',
  grossValue: 'Cumulative gross (latest/stream)',
  netValue: 'Net value',
  certifiedValue: 'Certified value',
  receivedValue: 'Received value',
  pendingValue: 'Pending value',
  agreementValueDisplay: 'Agreement value (scope)',
  agreementValueIsSingleProject: 'Single project scope',
};

export function formatMetricLabel(key) {
  if (key == null) return '';
  const s = String(key);
  if (METRIC_LABEL_OVERRIDES[s]) return METRIC_LABEL_OVERRIDES[s];
  const spaced = s
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  if (!spaced) return '';
  const acronyms = new Set(['qaqc', 'ra', 'bg', 'eot', 'mis', 'kpi']);
  return spaced
    .split(/\s+/)
    .map((w) => {
      const low = w.toLowerCase();
      if (acronyms.has(low)) return low === 'qaqc' ? 'QAQC' : low.toUpperCase();
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ');
}

export const fmtInr = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return `₹ ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(v))}`;
};

/** Same as fmtInr without the rupee prefix (use ₹ in column header). */
export const fmtInrNoSymbol = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(v));
};

/** Integer counts and plain numbers for KPI cells (avoids glued label/value confusion). */
export function formatCountValue(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isFinite(n)) return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n);
  return String(v);
}

export function buildCsv(rows, headers) {
  const esc = (v) => {
    const s = String(v ?? '');
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const line = (cells) => cells.map(esc).join(',');
  const out = [line(headers.map((h) => h.label))];
  for (const row of rows) {
    out.push(line(headers.map((h) => row[h.key])));
  }
  return out.join('\n');
}

export function downloadText(filename, text, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob(['\ufeff', text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** True when there are no project rows, no detail rows, and no positive numeric summary counts. */
export function moduleHasNoRecords(summary, byProject, details, extraDetailLists = []) {
  const hasProj = Array.isArray(byProject) && byProject.length > 0;
  const hasDet = Array.isArray(details) && details.length > 0;
  const hasExtra = extraDetailLists.some((a) => Array.isArray(a) && a.length > 0);
  if (hasProj || hasDet || hasExtra) return false;
  const s = summary && typeof summary === 'object' ? summary : {};
  return !Object.values(s).some((v) => typeof v === 'number' && v > 0);
}
